import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2, RotateCw, Send } from "lucide-react";
import { toast } from "sonner";

function useEmailStatus() {
  const [st, setSt] = useState(null);
  const load = useCallback(() => api.get("/admin/email/status").then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return [st, load];
}

export function EmailHealthBanner() {
  const [st, load] = useEmailStatus();
  const [busy, setBusy] = useState(false);
  if (!st?.failing) return null;
  const retry = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/email/retry"); toast[r.data.sent ? "success" : "error"](r.data.sent ? `${r.data.sent} email(s) sent` : "Still failing — see message"); load(); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-5 rounded-xl border border-red-300 bg-red-50 p-4 flex flex-wrap items-start gap-3" data-testid="email-health-banner">
      <AlertTriangle className="text-red-600 shrink-0" size={20} />
      <div className="flex-1 min-w-[240px] text-sm">
        <p className="font-bold text-red-800">Emails are not being delivered · {st.pending} queued</p>
        <p className="text-red-700 mt-0.5" data-testid="email-health-message">{st.message}</p>
      </div>
      <Button size="sm" variant="outline" onClick={retry} disabled={busy} data-testid="email-retry-now">
        {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RotateCw size={14} className="mr-1" />} Retry now
      </Button>
    </div>
  );
}

const BADGE = { sent: "bg-emerald-100 text-emerald-700", failed: "bg-red-100 text-red-700", skipped: "bg-slate-100 text-slate-600" };

export function EmailDiagnostics() {
  const [st, load] = useEmailStatus();
  const [busy, setBusy] = useState(false);
  const test = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/email/test", {});
      if (r.data.ok) toast.success(`Test email sent to ${r.data.to}`); else toast.error(r.data.message, { duration: 10000 });
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setBusy(false); load(); }
  };
  return (
    <div className="space-y-3" data-testid="email-diagnostics">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" onClick={test} disabled={busy} data-testid="email-send-test">
          {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />} Send test email
        </Button>
        {st && <span className="text-xs text-muted-foreground" data-testid="email-queue-count">{st.pending} email(s) queued for automatic retry (every 15 min)</span>}
      </div>
      {st?.recent?.length > 0 && (
        <div className="rounded-lg border divide-y text-xs max-h-60 overflow-y-auto" data-testid="email-recent-list">
          {st.recent.map((m) => (
            <div key={m.id} className="p-2 flex items-center gap-2">
              <span className={`px-1.5 py-0.5 rounded font-semibold ${BADGE[m.status] || ""}`}>{m.status}</span>
              <span className="flex-1 truncate">{m.subject} → {m.to || "(no recipient)"}</span>
              <span className="text-muted-foreground">{new Date(m.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
